import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {SettingsResponse} from '../../../_apiModels/_settingsModels'
import * as authHelper from './AuthHelpers'
import {AuthEmailModel, AuthModel, UserModel} from './_models'
import {getInstanceSettings, getUser, logoutUser, stopImpersonationUser} from './_requests'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  getSettingById: (setting: string | undefined, id: string | undefined) => any
  tempEmail: AuthEmailModel | undefined
  saveTempEmail: (auth: AuthEmailModel | undefined) => void
  userVerifyRoles: (roleToValidate: Array<string>) => boolean
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  currentInstanceSettings: SettingsResponse | undefined
  setInstanceSettings: Dispatch<SetStateAction<SettingsResponse | undefined>>
  logout: () => void
  stopImpersonation: () => void
  isAdmin: boolean
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  getSettingById: () => {},
  tempEmail: authHelper.getTempEmail(),
  saveTempEmail: () => {},
  userVerifyRoles: () => false,
  currentUser: undefined,
  setCurrentUser: () => {},
  currentInstanceSettings: undefined,
  setInstanceSettings: () => {},
  logout: () => {},
  stopImpersonation: () => {},
  isAdmin: false,
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [tempEmail, setTempEmail] = useState<AuthEmailModel | undefined>(authHelper.getTempEmail())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const [currentInstanceSettings, setInstanceSettings] = useState<SettingsResponse | undefined>()

  const saveAuth = async () => {
    // No need to save auth token anymore, it's handled by cookies
    try {
      const { data } = await getUser();
      const { data: instanceSettings } = await getInstanceSettings();
      if (data) {
        setCurrentUser(data);
        setInstanceSettings(instanceSettings);
      }
    } catch (error) {
      console.error('Failed to get user data', error);
      logout();
    }
  };


  const getSettingById = (setting: any | undefined, id: string | undefined) => {
    var dataList = currentInstanceSettings
      ? currentInstanceSettings[setting as keyof typeof currentInstanceSettings]
      : []
    if (dataList) {
      var data = dataList.find((x: any) => x.id === id)
      if (data) {
        return data
      }
    }
    return null
  }

  const saveTempEmail = (tempEmail: AuthEmailModel | undefined) => {
    setTempEmail(tempEmail)
    if (tempEmail) {
      authHelper.setTempEmail(tempEmail)
    } else {
      authHelper.removeAuth()
    }
  }

  const AdminRoles = {
    DeveloperAdministrator: 'Developer Administrator',
    BenchmarkingAdministrator: 'Benchmarking Administrator',
    ChristiansonBenchmarkingAdministrator: 'Christianson Benchmarking Administrator',
  }

  //Verify if user has valid roles assigned to their account.
  const userVerifyRoles = (rolesToValidate: Array<string>) => {
    rolesToValidate.push(AdminRoles.DeveloperAdministrator)
    rolesToValidate.push(AdminRoles.BenchmarkingAdministrator)
    rolesToValidate.push(AdminRoles.ChristiansonBenchmarkingAdministrator)
    if (currentUser) {
      return rolesToValidate.some((role) => currentUser?.roles?.includes(role))
    }
    return false
  }

  const isAdmin = userVerifyRoles([])

  // Add stopImpersonation function
  const stopImpersonation = async () => {
    try {
      await stopImpersonationUser();
      
      // Refresh user data after stopping impersonation
      const { data } = await getUser();
      const { data: instanceSettings } = await getInstanceSettings();
      if (data) {
        setCurrentUser(data);
        setInstanceSettings(instanceSettings);
      }
    } catch (error) {
      console.error('Stop impersonation error', error);
    }
  };

  
  const logout = async () => {
    try {
      await logoutUser();
      setCurrentUser(undefined);
      setInstanceSettings(undefined);
    } catch (error) {
      console.error('Logout error', error);
    }
  };


  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        getSettingById,
        tempEmail,
        saveTempEmail,
        userVerifyRoles,
        currentUser,
        setCurrentUser,
        currentInstanceSettings,
        setInstanceSettings,
        logout,
        stopImpersonation,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC = ({children}) => {
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const {auth, logout, setCurrentUser, setInstanceSettings} = useAuth()

  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data } = await getUser();
        const { data: instanceSettings } = await getInstanceSettings();
        if (data) {
          setCurrentUser(data);
          setInstanceSettings(instanceSettings);
        }
      } catch (error) {
        console.error('Failed to initialize auth', error);
        logout();
      } finally {
        setShowSplashScreen(false);
      }
    };

    initAuth();
  }, []);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>;
};


export {AuthInit, AuthProvider, useAuth}
